import { createSlice, PayloadAction } from '@reduxjs/toolkit';

type snackbarSeverity = 'info' | 'success' | 'warning' | 'error';

export type ColorThemes = 'legacyDark' | 'legacyLight' | 'dark' | 'light' | '';

export type SnackbarArgs = {
    message: string;
    severity?: snackbarSeverity;
    /** If true, autoHideDuration is ignored and the alert is dismissed only by user action. */
    alertIsSticky?: boolean;
};
export interface UiState {
    animatingViewTransition: boolean;
    menuBarActive: boolean | 'always';
    isMobile: boolean;
    snackbar: {
        message: string;
        isActive: boolean;
        severity: snackbarSeverity;
        /** If true, autoHideDuration is ignored and the alert is dismissed only by user action. */
        alertIsSticky?: boolean;
    };
    theme: ColorThemes;
    width: number;
    height: number;
}

const initialState: UiState = {
    animatingViewTransition: false,
    menuBarActive: false,
    isMobile: false,
    snackbar: {
        message: '',
        isActive: false,
        severity: 'info',
        alertIsSticky: false,
    },
    theme: '',
    width: 1920,
    height: 1080,
};

export const uiSlice = createSlice({
    name: 'ui',
    initialState,
    reducers: {
        viewAnimationOn: (state) => {
            state.animatingViewTransition = true;
        },
        viewAnimationOff: (state) => {
            state.animatingViewTransition = false;
        },
        toggleMenuBar: (state) => {
            state.menuBarActive = !state.menuBarActive;
        },
        menuBarOff: (state) => {
            state.menuBarActive = false;
        },
        menuBarOn: (state) => {
            state.menuBarActive = true;
        },
        menuBarAlwaysOn: (state) => {
            state.menuBarActive = 'always';
        },
        mobileModeOn: (state) => {
            if (!state.isMobile) state.menuBarActive = false;
            state.isMobile = true;
        },
        mobileModeOff: (state) => {
            state.isMobile = false;
            state.menuBarActive = 'always';
        },
        activateSnackbar: (state, action: PayloadAction<SnackbarArgs>) => {
            state.snackbar.message = action.payload.message;
            state.snackbar.isActive = true;
            if (action.payload.severity) {
                state.snackbar.severity = action.payload.severity;
            }
            if (action.payload.alertIsSticky) {
                state.snackbar.alertIsSticky = action.payload.alertIsSticky;
            }
        },
        clearSnackbar: (state) => {
            state.snackbar.message = '';
            state.snackbar.isActive = false;
            state.snackbar.severity = 'info';
            state.snackbar.alertIsSticky = false;
        },
        setTheme: (state, action) => {
            state.theme = action.payload;
        },
        setSize: (state, action) => {
            state.width = action.payload.width;
            state.height = action.payload.height;
        },
    },
});

export const {
    viewAnimationOn,
    viewAnimationOff,
    toggleMenuBar,
    menuBarOff,
    menuBarOn,
    menuBarAlwaysOn,
    mobileModeOn,
    mobileModeOff,
    activateSnackbar,
    clearSnackbar,
    setTheme,
    setSize,
} = uiSlice.actions;
